import { logger } from "@/lib/logger"
import type { JsonObject } from "@/types/json"
import type { TestCaseInput } from "@/types/TestCaseInput"
import { TestSetType, type TestSetInput } from "@/types/TestSetInput"
import { stringifyUnknownValue } from "@/utils/stringifyUnknownValue"

import { MAX_TEST_SET_DISPLAY_NAME_LENGTH } from "../constants"

const log = logger("csv-upload-utils")

interface CsvUploadTestCaseInput extends TestCaseInput {
  id: string
}

export interface CsvUploadTestSetInput extends Omit<
  TestSetInput,
  "display_name" | "test_set_metadata" | "test_set_type"
> {
  displayName: string
  testSetMetadata: JsonObject
  testSetType: TestSetType
  testCases: CsvUploadTestCaseInput[]
  agentIds: string[]
}

interface ProcessCsvRowsOptions {
  datasetName: string
  fileName: string
  skipEmptyInput?: boolean
}

export const getTestSetDisplayNameFromFileName = (fileName: string): string =>
  fileName.replace(/\.[^/.]+$/, "")

export const getTestSetDisplayNameValidationError = (
  fileName: string,
): string | null => {
  const displayName = getTestSetDisplayNameFromFileName(fileName)
  if (Array.from(displayName).length <= MAX_TEST_SET_DISPLAY_NAME_LENGTH) {
    return null
  }

  return `File name is too long. Rename the file so its name, excluding the extension, is ${MAX_TEST_SET_DISPLAY_NAME_LENGTH} characters or fewer.`
}

const getCellText = (value: unknown): string =>
  value == null ? "" : stringifyUnknownValue(value)

const isValidTestSetType = (value: string): value is TestSetType =>
  Object.values(TestSetType).includes(value as TestSetType)

const inferType = (input: string): TestSetType => {
  try {
    const parsed: unknown = JSON.parse(input)
    if (Array.isArray(parsed)) {
      return TestSetType.TRANSCRIPT
    }
    if (typeof parsed === "object" && parsed !== null) {
      if ("action_graph" in parsed) {
        return TestSetType.IVR
      }
      return TestSetType.WORKFLOW
    }
  } catch {
    // If JSON parsing fails, it's not a JSON input
  }
  return TestSetType.SCENARIO
}

const parseExpectedBehaviors = (value: unknown): string[] => {
  if (value === undefined || value === null) {
    return []
  }

  const raw = stringifyUnknownValue(value).trim()
  if (!raw) {
    return []
  }

  try {
    const parsed: unknown = JSON.parse(raw)
    if (Array.isArray(parsed)) {
      return parsed
        .map((item) => String(item).trim())
        .filter((item) => item.length > 0)
    }
  } catch {
    // Not a JSON array, try to parse as CSV
  }

  const splitCandidates = raw
    .split(/[\n,]/)
    .map((behavior) => behavior.trim())
    .filter((behavior) => behavior.length > 0)

  if (splitCandidates.length > 1) {
    return splitCandidates
  }

  return [raw]
}

const parseAgentIds = (value: unknown): string[] => {
  if (value === undefined || value === null) {
    return []
  }

  const raw = stringifyUnknownValue(value).trim()
  if (!raw) {
    return []
  }

  try {
    const parsed: unknown = JSON.parse(raw)
    if (Array.isArray(parsed)) {
      return parsed.map((id) => String(id).trim()).filter((id) => id.length > 0)
    }
  } catch {
    // Not a JSON array, fall back to comma-separated parsing for backward compatibility
  }

  return raw
    .split(",")
    .map((id) => id.trim())
    .filter((id) => id.length > 0)
}

export const processCsvRowsToTestSetInput = (
  rows: unknown[][],
  options: ProcessCsvRowsOptions,
): CsvUploadTestSetInput => {
  const { datasetName, fileName, skipEmptyInput = true } = options

  if (!rows.length || !rows[0]) {
    throw new Error("Uploaded CSV is empty")
  }

  const dataRows = rows.slice(1)

  const headers = rows[0].map((header) =>
    getCellText(header).toLowerCase().trim(),
  )
  const originalHeaders = rows[0].map((header) => getCellText(header).trim())
  const typeIndex = headers.indexOf("type")
  const inputIndex = headers.indexOf("input")
  const expectedBehaviorsIndex = headers.indexOf("expected_behaviors")
  let expectedIndex = headers.indexOf("expected")
  if (expectedIndex === -1) {
    expectedIndex = headers.indexOf("expected_output")
  }
  if (expectedIndex === -1) {
    expectedIndex = headers.indexOf("expected output")
  }

  if (inputIndex === -1) {
    throw new Error("CSV must contain an 'input' column")
  }

  const metadataJsonIndex = headers.indexOf("metadata")
  const agentIdsIndex = headers.indexOf("agent_ids")

  const reservedColumns = new Set([
    "type",
    "input",
    "expected",
    "expected_behaviors",
    "expected_output",
    "expected output",
    "metadata",
    "agent_ids",
  ])

  const metadataColumnIndices: { index: number; name: string }[] = []
  headers.forEach((header, index) => {
    if (header && !reservedColumns.has(header)) {
      metadataColumnIndices.push({ index, name: originalHeaders[index] })
    }
  })

  let agentIdsFromCSV: string[] = []
  if (agentIdsIndex !== -1) {
    for (const row of dataRows) {
      const agentIdsValue = getCellText(row[agentIdsIndex]).trim()
      if (agentIdsValue.length > 0) {
        const parsedAgentIds = parseAgentIds(agentIdsValue)
        if (parsedAgentIds.length > 0) {
          agentIdsFromCSV = parsedAgentIds
          break
        }
      }
    }
  }

  const testCases = dataRows
    .flatMap((row, rowIndex) => {
      if (!skipEmptyInput) {
        return [{ row, csvRowNumber: rowIndex + 2 }]
      }

      const inputStr = getCellText(row[inputIndex])
      return inputStr.trim().length > 0
        ? [{ row, csvRowNumber: rowIndex + 2 }]
        : []
    })
    .map(({ row, csvRowNumber }, index) => {
      const inputStr = getCellText(row[inputIndex])
      const explicitType =
        typeIndex !== -1 && row[typeIndex]
          ? getCellText(row[typeIndex]).toUpperCase()
          : ""
      const inputType =
        explicitType && isValidTestSetType(explicitType)
          ? explicitType
          : inferType(inputStr)

      let expectedOutputJson: JsonObject | null = null

      if (metadataJsonIndex !== -1 && row[metadataJsonIndex]) {
        const metadataValue = getCellText(row[metadataJsonIndex]).trim()
        if (metadataValue) {
          try {
            const parsed: unknown = JSON.parse(metadataValue)
            if (parsed && typeof parsed === "object") {
              expectedOutputJson = parsed as JsonObject
            }
          } catch {
            log.warn(`Failed to parse metadata JSON in row ${csvRowNumber}`, {
              metadataValue,
            })
          }
        }
      }

      if (!expectedOutputJson && metadataColumnIndices.length > 0) {
        const metadata: JsonObject = {}
        metadataColumnIndices.forEach(({ index: colIndex, name }) => {
          const value = row[colIndex]
          if (value !== undefined && value !== null && value !== "") {
            try {
              metadata[name] = JSON.parse(getCellText(value)) as JsonObject
            } catch {
              metadata[name] = getCellText(value)
            }
          }
        })
        if (Object.keys(metadata).length > 0) {
          expectedOutputJson = metadata
        }
      }

      const rawExpectedValue =
        expectedBehaviorsIndex !== -1
          ? row[expectedBehaviorsIndex]
          : expectedIndex !== -1
            ? row[expectedIndex]
            : ""

      return {
        id: String(index + 1),
        input_str: inputStr,
        expected_behaviors: parseExpectedBehaviors(rawExpectedValue),
        s3_bucket: "",
        s3_key: "",
        input_type: inputType,
        expected_output_json: expectedOutputJson,
      }
    })

  return {
    displayName: datasetName,
    description: `Imported from ${fileName}`,
    testSetMetadata: {
      owner: "",
    },
    testCases,
    testSetType: TestSetType.SCENARIO,
    agentIds: agentIdsFromCSV,
  }
}
